import { format } from "date-fns";
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { clinicianAdlItems, useAdlItems } from "@/hooks/queries/useAdlItems";
import { ApiError } from "@/lib/api-client";
import { useAmpsTasks } from "@/hooks/queries/useAmpsTasks";
import { useCalibrationCaseLog } from "@/hooks/queries/useCalibrationCaseLog";
import { CalibrationSubNav } from "@/routes/calibration/CalibrationSubNav";
import { CaseVideo } from "@/routes/calibration/CaseVideo";
import { ScoreEntryGrid } from "@/routes/evaluation/ScoringStep";
import { taskLabel } from "@/routes/calibration/task-label";

// Story 8.8 (gh-247) — read-only calibration case log (UX Screen 18). The scored
// card's "View case log" CTA (Story 8.7 board) lands here: the caller's OWN scored
// submission (SELF-only wire, AC1) rendered as case context + the real case video
// (seam-82) + the Story 6.5 ScoreEntryGrid in its read-only variant. NO edit/submit/
// clear affordances anywhere — the "Read only" pill signals the state explicitly
// (never styling alone, AC8).
//
// Deliberate deviations from the mockup (AC7 — do NOT "fix" back):
//   • single task label (the real gold standard scores ONE task per case — Story 4.6
//     Decision #3; the mockup's second label is stale-AC fiction, AR30 tracks it);
//   • a real HTML5 player replaces the static "Case video placeholder" text
//     (checkpoint D2) — with DevOps #430 unprovisioned the read 503s (CAL-503) and
//     the calm Banner below IS the correct interim;
//   • AppShell + CalibrationSubNav in-content sub-nav, not the mockup's standalone
//     calibration sidebar (the approved 8.7 layout reconciliation).

// The video leg (seam-82, AC4) moved to the shared `CaseVideo` at Story 8.10 —
// the scoring workspace (Screen 17) mounts the same player, so the pre-signed-URL
// lifecycle and failure posture live in ONE place.

export function CalibrationCaseLogScreen() {
  const { caseId } = useParams<{ caseId: string }>();
  const logQuery = useCalibrationCaseLog(caseId);
  const tasksQuery = useAmpsTasks();
  const adlItemsQuery = useAdlItems();

  const items = adlItemsQuery.data ? clinicianAdlItems(adlItemsQuery.data) : [];
  const log = logQuery.data;
  // Only a real CAL-LOG-404 means "not scored yet" — a transient 500/network
  // failure must never claim the clinician's scored case doesn't exist (AC9).
  const notScored =
    logQuery.error instanceof ApiError && logQuery.error.code === "CAL-LOG-404";

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">
          {log ? `Case ${log.order}` : "Case log"}
        </h1>
      </header>

      <CalibrationSubNav />

      {/* AC9 posture (the 8.7 sibling): quiet while pending; a 404 (not scored /
          unknown case — CAL-LOG-404) or any failed read renders a calm info Banner
          + the back CTA, never a broken grid or an alarming error. */}
      {logQuery.isPending ? null : !log ? (
        <>
          {notScored ? (
            <Banner variant="info" title="Case log unavailable">
              <p>
                There’s no scored submission to show for this case yet. Scored
                cases appear here once processing completes.
              </p>
            </Banner>
          ) : (
            <Banner variant="info" title="Case log couldn’t load">
              <p>
                The case log couldn’t be loaded right now. Your recorded scores
                are safe — try again in a moment.
              </p>
            </Banner>
          )}
          <div>
            <Button asChild variant="secondary">
              <Link to="/calibration/cases">Back to case studies</Link>
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
              Case context
            </h2>
            <ul className="flex flex-col gap-2 text-sm text-[color:var(--text-secondary)]">
              <li className="flex items-center gap-2">
                <strong className="text-[color:var(--text-primary)]">Status:</strong>
                <Badge variant="ok">Scored</Badge>
              </li>
              <li>
                <strong className="text-[color:var(--text-primary)]">Task:</strong>{" "}
                {taskLabel(tasksQuery.data, log.task_code)}
              </li>
              <li>
                <strong className="text-[color:var(--text-primary)]">Submitted:</strong>{" "}
                <span className="tabular-nums">
                  {format(new Date(log.submitted_at), "hh:mm a")}
                </span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
                Task data
              </h2>
              {/* The explicit read-only signal (AC8) — never styling alone. */}
              <Badge variant="info">Read only</Badge>
            </div>

            {/* key on caseId so the player's mount-scoped remint latch (CaseVideo)
                resets per case — a prior case's remint must not block the next. */}
            <CaseVideo key={log.case_id} caseId={log.case_id} />

            <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-5">
              <div className="mb-4 flex flex-col gap-1 border-b border-[color:var(--border-primary)] pb-3">
                <strong className="text-sm font-semibold text-[color:var(--text-primary)]">
                  Scores
                </strong>
                <span className="text-sm text-[color:var(--text-tertiary)]">
                  {taskLabel(tasksQuery.data, log.task_code)}
                </span>
              </div>
              {/* The 8.3 submit contract: `scores` is positional over the SAME
                  clinician 35-item order the grid lays out — no re-mapping. A
                  failed dictionary read gets its own calm Banner (AC9) — never a
                  silently absent grid under an otherwise-healthy screen. */}
              {adlItemsQuery.isError ? (
                <Banner variant="info" title="Recorded scores unavailable">
                  <p>
                    The score list couldn’t be loaded right now. Your recorded
                    scores are safe — try again in a moment.
                  </p>
                </Banner>
              ) : items.length > 0 ? (
                <ScoreEntryGrid
                  items={items}
                  scores={log.scores}
                  taskPosition={1}
                  gridLabel="Recorded scores for the case task (read only)"
                  readOnly
                />
              ) : null}
            </div>
          </div>

          <div>
            <Button asChild variant="secondary">
              <Link to="/calibration/cases">Back to case studies</Link>
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

export default CalibrationCaseLogScreen;
